import {
  Stack,
  Checkbox,
  Group,
  TextInput,
  Textarea,
  NumberInput,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { CancelButton, SaveButton } from "../../../../-components/Buttons"
import {
  AddCalendarProfile,
  UpdateCalendarProfile,
} from "../../../../@client/Theasoft_ApiServer_WebCalendar"
import { WebCalendarProfileDto } from "../../../../@client/Theasoft_Shared_Contracts_WebCalendar"
import { ErrorAlert } from "../../../../blocks/errors/error-alert"
import { pathProxy } from "../../../../utils/forms-proxy"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { usePostMutation } from "../../../../typed-client/hooks/use-post-mutation"
import { DefaultModal } from "../../../../-components/default-modal"

interface Props {
  profile: WebCalendarProfileDto | null
  onClose: () => void
  addCalendarProfilePathProxy: ReturnType<typeof pathProxy<AddCalendarProfile>>
}

export function EditCalendarProfileModal({
  profile,
  onClose,
  addCalendarProfilePathProxy,
}: Props) {
  const { t } = useTranslation("app_server")
  const client = useQueryClient()
  const form = useForm<UpdateCalendarProfile>({})

  React.useEffect(() => {
    if (profile !== null) {
      form.setInitialValues({
        profile: {
          description: profile.description || "",
          id: profile.id,
          name: profile.name,
          webCalendarProfileDataProviderId: "",
        },
        settings: profile.settings,
      })
      form.reset()
    }
  }, [profile])
  const updateProfile = usePostMutation(
    "/api/web-calendar/update-personal-web-calendar-profile",
    {
      onSuccess: () => {
        onClose()
        client.invalidateQueries({
          queryKey: ["/api/web-calendar/get-profiles", null],
        })
      },
    }
  )
  return (
    <DefaultModal
      size="lg"
      title={t("Calendar_Subscriptions_EditModalTitle")}
      opened={profile != null}
      onClose={onClose}
    >
      {/* <Debug _show {...profile} /> */}
      <form onSubmit={form.onSubmit((values) => updateProfile.mutate(values))}>
        <Stack gap="xs">
          <Checkbox.Group
            label={t("AppointmentTypes")}
            description={t(
              "Calendar_Subscriptions_AppointmentTypesDescription"
            )}
          >
            {/* empty CheckboxGroup just for the label and description, else it would control the state (we want each checkbox to control a property) */}
            <div />
          </Checkbox.Group>
          <Group>
            <Checkbox
              label={t("Absences")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.includeAbsenceAppointments
                  ._PATH_,
                { type: "checkbox" }
              )}
            />
            <Checkbox
              label={t("Casts")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.includeCastAppointments
                  ._PATH_,
                { type: "checkbox" }
              )}
            />
            <Checkbox
              label={t("DutyRosters")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings
                  .includeDutyRosterAppointments._PATH_,
                { type: "checkbox" }
              )}
            />
            <Checkbox
              label={t("Travels")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.includeTravelAppointments
                  ._PATH_,
                { type: "checkbox" }
              )}
            />
          </Group>

          <TextInput
            autoFocus
            label={t("Name")}
            data-autofocus
            placeholder={t("Calendar_Subscriptions_CreateModalNamePlaceholder")}
            {...form.getInputProps(
              addCalendarProfilePathProxy.profile.name._PATH_
            )}
          />
          <Textarea
            label={t("Description")}
            placeholder={t(
              "Calendar_Subscriptions_CreateModalDescriptionPlaceholder"
            )}
            {...form.getInputProps(
              addCalendarProfilePathProxy.profile.description._PATH_
            )}
          />
          <Group>
            <NumberInput
              label={t("NumberOfDaysInThePast")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.daysOffsetInPast._PATH_
              )}
            />
            <NumberInput
              label={t("NumberOfDaysInTheFuture")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.daysOffsetInFuture._PATH_
              )}
            />
          </Group>
          <ErrorAlert error={updateProfile.error} />
          <Group mt="md">
            <CancelButton onClick={onClose} />
            <SaveButton loading={updateProfile.isPending} />
          </Group>
        </Stack>
      </form>
    </DefaultModal>
  )
}
